<template>
  <div class="md" v-html="sanitizedHtml"></div>
</template>

<script setup>
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import { marked } from 'marked'

const props = defineProps({
  source: { type: String, default: '' },
})

const sanitizedHtml = computed(() => {
  const src = String(props.source || '')
  let html = ''
  try {
    html = marked.parse(src, {
      gfm: true,
      breaks: true,
      headerIds: false,
      mangle: false,
    })
  } catch {
    html = ''
  }

  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target', 'rel'],
  })
})
</script>

<style scoped>
.md {
  font-size: 13px;
  line-height: 1.45;
  color: var(--md-text);
  word-break: break-word;
}

.md :deep(p) {
  margin: 0;
}

.md :deep(p + p) {
  margin-top: 8px;
}

.md :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  padding: 1px 6px;
  border-radius: 6px;
  background: var(--md-inline-code-bg);
  border: 1px solid var(--border);
}

.md :deep(pre) {
  margin: 8px 0;
  padding: 10px;
  border-radius: 10px;
  background: var(--md-pre-bg);
  color: var(--md-pre-text);
  overflow: auto;
  border: 1px solid var(--md-pre-border);
}

.md :deep(pre code) {
  background: transparent;
  border: none;
  padding: 0;
  color: inherit;
  font-size: 12px;
}

.md :deep(a) {
  color: var(--link);
  text-decoration: underline;
}

.md :deep(ul),
.md :deep(ol) {
  margin: 8px 0;
  padding-left: 18px;
  list-style-position: outside;
}

.md :deep(ul ul),
.md :deep(ol ol),
.md :deep(ul ol),
.md :deep(ol ul) {
  margin-top: 6px;
  margin-bottom: 6px;
}

.md :deep(li + li) {
  margin-top: 4px;
}

.md :deep(blockquote) {
  margin: 8px 0;
  padding-left: 10px;
  border-left: 3px solid var(--border);
  color: var(--muted);
}
</style>
